import { sanitizePublicMessage } from "@/utils/errors/publicErrorContract";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { getBackendOrigin } from "@/utils/network/getBackendOrigin";

export type ApiHealthStatus = "ok" | "degraded" | "unhealthy";

interface BackendHealthResponse {
  status?: string;
}

type HealthEndpoint = "/api-health" | "/health";
const HEALTH_CHECK_TIMEOUT_MS = 10_000;

const toSafeErrorMessage = (error: unknown): string =>
  sanitizePublicMessage(error instanceof Error ? error.message : String(error));

const resolveCorrelationIdFromResponse = (
  response: Response,
  fallbackCorrelationId: string,
): string => {
  const responseCorrelationId =
    response.headers.get("x-correlation-id") ??
    response.headers.get("X-Correlation-ID");

  const normalizedCorrelationId = responseCorrelationId?.trim();
  if (!normalizedCorrelationId) {
    return fallbackCorrelationId;
  }

  return normalizedCorrelationId;
};

/**
 * Probes the backend health endpoints and returns a simple healthy/unhealthy
 * verdict for the global admin availability gate.
 *
 * We try `/api-health` first and only fall back to `/health` when the primary
 * route is missing so local/dev environments keep working without masking real
 * outages.
 */
export const pingHealth = async (
  ownerSignal?: AbortSignal,
): Promise<ApiHealthStatus> => {
  const logger =
    FrontendLoggerFactory.getLogger().withComponent("ApiHealthService");
  const correlationId = generateCorrelationId();
  const requestLogger = logger.withCorrelationId(correlationId);
  const origin = getBackendOrigin();
  if (ownerSignal?.aborted) return "unhealthy";

  const abortController = new AbortController();
  const abortFromOwner = () => abortController.abort(ownerSignal?.reason);
  ownerSignal?.addEventListener("abort", abortFromOwner, { once: true });
  const timeoutState: { id?: number } = {};
  let rejectOnAbort = () => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectOnAbort = () => {
      const reason = abortController.signal.reason;
      reject(
        reason instanceof Error
          ? reason
          : new DOMException("Aborted", "AbortError"),
      );
    };
    abortController.signal.addEventListener("abort", rejectOnAbort, {
      once: true,
    });
  });
  // The deadline must also settle the gate if a response/body implementation
  // does not itself reject when the signal is aborted.
  const awaitProbe = <T>(work: Promise<T>): Promise<T> =>
    Promise.race([work, aborted]);

  const buildUrl = (endpoint: HealthEndpoint) =>
    origin ? `${origin}${endpoint}` : endpoint;

  const tryFetchHealth = async (endpoint: HealthEndpoint) => {
    const url = buildUrl(endpoint);
    requestLogger.debug("Performing backend health check", {
      operation: "health_check_request",
      url,
      endpoint,
      timeoutMs: HEALTH_CHECK_TIMEOUT_MS,
    });

    window.clearTimeout(timeoutState.id);
    timeoutState.id = window.setTimeout(() => {
      abortController.abort(
        new Error(`Health check timed out after ${HEALTH_CHECK_TIMEOUT_MS}ms`),
      );
    }, HEALTH_CHECK_TIMEOUT_MS);

    const response = await awaitProbe(
      fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          [CORRELATION_ID_HEADER]: correlationId,
        },
        signal: abortController.signal,
      }),
    );

    return {
      endpoint,
      response,
      url,
    };
  };

  try {
    const primary = await tryFetchHealth("/api-health");
    const shouldFallback =
      !primary.response.ok &&
      (primary.response.status === 404 || primary.response.status === 405);

    const { response, url } = shouldFallback
      ? await tryFetchHealth("/health")
      : primary;
    const responseCorrelationId = resolveCorrelationIdFromResponse(
      response,
      correlationId,
    );
    const responseLogger = requestLogger.withCorrelationId(
      responseCorrelationId,
    );

    if (shouldFallback) {
      responseLogger.notice("Health check fell back to legacy endpoint", {
        operation: "health_check_fallback_endpoint",
        fallbackEndpoint: "/health",
        primaryEndpoint: "/api-health",
      });
    }

    if (!response.ok) {
      responseLogger.warn("Health check returned non-OK status", {
        operation: "health_check_non_ok",
        status: response.status,
        statusText: response.statusText,
        url,
      });
      return "unhealthy";
    }

    let body: BackendHealthResponse | null = null;

    try {
      body = (await awaitProbe(response.json())) as BackendHealthResponse;
    } catch (error) {
      if (abortController.signal.aborted) throw error;
      responseLogger.warn("Health check response could not be parsed as JSON", {
        operation: "health_check_parse_error",
        url,
        error: toSafeErrorMessage(error),
      });
      return "unhealthy";
    }

    const status = typeof body.status === "string" ? body.status : "";
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === "healthy") {
      responseLogger.debug("Backend reported healthy status", {
        operation: "health_check_success",
        url,
      });
      return "ok";
    }

    if (normalizedStatus === "degraded") {
      responseLogger.warn("Backend reported degraded status", {
        operation: "health_check_degraded_status",
        url,
      });
      return "degraded";
    }

    responseLogger.warn("Backend health status not healthy (but HTTP OK)", {
      operation: "health_check_unhealthy_status",
      url,
      backendStatus: status || "unknown",
    });
    return "unhealthy";
  } catch (error) {
    if (ownerSignal?.aborted) return "unhealthy";
    const isTimeoutError =
      error instanceof Error &&
      /timed out|abort/i.test(error.message || error.name);
    if (isTimeoutError) {
      requestLogger.warn(
        "Health check timed out before the backend responded",
        {
          operation: "health_check_timeout",
          url: buildUrl("/api-health"),
          timeoutMs: HEALTH_CHECK_TIMEOUT_MS,
        },
      );
    }
    requestLogger.error(
      "Health check failed with network or runtime error",
      error instanceof Error ? error : new Error(String(error)),
      {
        operation: "health_check_error",
        url: buildUrl("/api-health"),
        error: toSafeErrorMessage(error),
      },
    );
    return "unhealthy";
  } finally {
    window.clearTimeout(timeoutState.id);
    abortController.signal.removeEventListener("abort", rejectOnAbort);
    ownerSignal?.removeEventListener("abort", abortFromOwner);
  }
};
